import { useEffect, useState } from 'react';
import { DateRange } from 'react-date-range';
import {
  Box,
  ClickAwayListener,
  Popover,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
} from '@mui/material';
import { enUS } from 'date-fns/locale';
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { format } from 'date-fns';
import { DATE_FORMAT } from '../../utils/constants';

export default function DateRangePicker({
  label,
  value,
  onChange,
  relativeDateRanges = {},
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  const [relativeRange, setRelativeRange] = useState(null);
  const [absoluteRange, setAbsoluteRange] = useState({
    startDate: null,
    endDate: null,
    key: 'selection',
  });

  useEffect(() => {
    if (value?.key) {
      const range = relativeDateRanges[value.key];
      setRelativeRange(value.key);
      setAbsoluteRange({
        startDate: range.startDate,
        endDate: range.endDate,
        key: 'selection',
      });
    } else if (value?.startDate && value?.endDate) {
      setRelativeRange(null);
      setAbsoluteRange({
        startDate: new Date(value.startDate),
        endDate: new Date(value.endDate),
        key: 'selection',
      });
    } else {
      setRelativeRange(null);
      setAbsoluteRange({
        startDate: null,
        endDate: null,
        key: 'selection',
      });
    }
  }, [value, relativeDateRanges]);

  const handleAbsoluteRangeChange = (ranges) => {
    const dates = ranges.selection;
    setAbsoluteRange(dates);
    setRelativeRange(null);

    onChange?.({
      type: 'absolute',
      startDate: dates.startDate,
      endDate: dates.endDate,
    });
  };

  const handleRelativeRangeChange = (key, startDate, endDate) => {
    setAbsoluteRange({ startDate, endDate, key: 'selection' });
    setRelativeRange(key);

    onChange?.({ type: 'relative', key });
  };

  const handleResetDateRange = () => {
    setAbsoluteRange({ startDate: null, endDate: null, key: 'selection' });
    setRelativeRange(null);

    onChange?.({
      type: null,
      key: null,
      startDate: null,
      endDate: null,
    });
  };

  const renderAdornmentContent = () => {
    if (relativeRange) {
      const selectedLabel = relativeDateRanges[relativeRange]?.label || '';

      return (
        <InputAdornment position="start">
          <Chip label={selectedLabel} size="small" sx={{ cursor: 'pointer' }} />
        </InputAdornment>
      );
    }

    if (absoluteRange.startDate && absoluteRange.endDate) {
      return (
        <InputAdornment position="start">
          <Box sx={{ display: 'flex', gap: 1, cursor: 'pointer' }}>
            <Chip label={format(absoluteRange.startDate, DATE_FORMAT.RANGE_DATE_PICKER)} size="small" />
            <Chip label={format(absoluteRange.endDate, DATE_FORMAT.RANGE_DATE_PICKER)} size="small" />
          </Box>
        </InputAdornment>
      );
    }

    return null;
  };

  return (
    <Box width="100%">
      <TextField
        label={label}
        variant="outlined"
        fullWidth
        focused={!!(absoluteRange.startDate && absoluteRange.endDate)}
        onClick={handleOpen}
        InputProps={{
          readOnly: true,
          startAdornment: renderAdornmentContent(),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton>
                <InsertInvitationIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
          sx: { cursor: 'pointer' },
        }}
        sx={{
          '& input': {
            pointerEvents: 'none',
            cursor: 'pointer',
          },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Box sx={{ display: 'flex', p: 2 }}>
            <Stack
              direction="column"
              justifyContent="space-between"
              mr={2}
            >
              <Stack direction="column" gap={1}>
                {Object.entries(relativeDateRanges).map(([key, data]) => (
                  <Chip
                    key={key}
                    label={data.label}
                    onClick={() => handleRelativeRangeChange(key, data.startDate, data.endDate)}
                    variant="contained"
                    clickable
                  />
                ))}
              </Stack>

              <Chip
                label="Reset"
                onClick={handleResetDateRange}
                variant="outlined"
                clickable
              />
            </Stack>

            <DateRange
              months={1}
              locale={enUS}
              direction="horizontal"
              showDateDisplay={false}
              moveRangeOnFirstSelection={false}
              onChange={handleAbsoluteRangeChange}
              ranges={[{
                ...absoluteRange,
                startDate: absoluteRange.startDate || new Date(),
                endDate: absoluteRange.endDate || new Date(),
              }]}
            />
          </Box>
        </ClickAwayListener>
      </Popover>
    </Box>
  );
}
